<template lang="pug">
div.x-cell
  //- Renderizando a célula da barra de ações.
  template(v-if="isActionBar")

    //- MENU
    action-bar-ex-menu(
      v-if="hasMenu && isLeftActionBar"
      v-bind="$props"
      x-small
      :entity="entity"
      :data="data"
      :row="row"
      :actions="allActions"
      @perform="opts => $emit('perform', { ...opts, selection: row })"
    )

    //- AÇÕES PINADAS
    action-bar-ex-pin(
      v-for="(action,index) in pins"
      x-small
      no-margin
      :key="`pin-${index}`"
      :action="action"
      :entity="entity"
      :data="data"
      :row="row"
      @perform="opts => $emit('perform', { ...opts, selection: row })"
    )

    //- MENU
    action-bar-ex-menu(
      v-if="hasMenu && isRightActionBar"
      v-bind="$props"
      x-small
      :entity="entity"
      :data="data"
      :row="row"
      :actions="allActions"
      @perform="opts => $emit('perform', { ...opts, selection: row })"
    )

  //- Renderizando uma célula de valor definido com tooltip declarado.
  v-tooltip(
    bottom
    :disabled="!tooltip.enabled"
  )
    template(#activator="{ on, attrs }")
      span(
        v-bind="attrs"
        v-on="on"
        :color="color"
        :class="clazz"
        :style="style"
      )
        v-icon(
          v-if="icon"
          small
          :left="!iconOnly"
          :color="color"
          :class="clazz"
          :style="style"
        )
          | {{ icon }}

        template(v-if="!iconOnly")
          router-link.x-link(
            v-if="hasLink"
            is="a",
            @click.stop="call(link)"
          )
            x-cell(
              v-model="formattedValue"
              :col="col"
              :cell="cell"
              :cell-link="link"
              :cell-class="clazz"
              :cell-color="color"
              :cell-style="style"
            )

          span(v-else)
            x-cell(
              v-model="formattedValue"
              :col="col"
              :cell="cell"
              :cell-link="link"
              :cell-class="clazz"
              :cell-color="color"
              :cell-style="style"
            )

    span(
      :class="tooltip.clazz",
      :color="tooltip.color",
      :style="tooltip.style"
    )
      span.text-md.font-weight-medium(v-if="tooltip.title") {{ tooltip.title }}
      br(v-if="tooltip.title && tooltip.description")
      span.font-weight-light(v-if="tooltip.description") {{ tooltip.description }}

</template>

<style lang="scss">
.x-cell {
  .x-link {
    color: inherit;
  }
  .x-link:hover {
    text-decoration: underline;
  }
  .x-badge-cell {
    font-weight: 600;
  }
  /* Estilos de texto */
  .x-bold { font-weight: bold !important; }
  .x-italic { font-style: italic !important; }
  .x-overline { text-decoration: overline !important; }
  .x-strike-through { text-decoration: line-through !important; }
  .x-underline { text-decoration: underline !important; }
  .x-monospace { font-family: 'Courier New', Courier, monospace !important; }
}
</style>

<script>
import XChip from "./XChip"
import XCell from "./XCell"
import ActionBarExMenu from "@/components/commons/action-bar-ex/ActionBarExMenu"
import ActionBarExPin from '@/components/commons/action-bar-ex/ActionBarExPin'
import { normalizeToLowercase } from "@/helpers/strings"
import { selectOptions, selectClass, selectColor, selectFont } from '@/helpers/styles'
import { getDate, getDateTime } from '@/helpers/dates'
import { mergeActions } from '@/helpers/compatibility/gridex'
import { findRel } from '@/helpers/rels'
import { parseEref } from '@/helpers/erefs'

export default {
  components: {
    XChip,
    XCell,
    ActionBarExMenu,
    ActionBarExPin,
  },

  props: {
    site: String,
    eref: String,
    form: Object,

    /**
     * A entidade em exibição.
     * @type {Keep.Paper.Design.Paper}
     */
    entity: Object,

    /**
     *
     * @type {Object}
     */
    data: Object,

    /**
     *
     * @type {Object}
     */
    row: Object,

    /**
     * A coleção de registros exibidos.
     * @type {Keep.Paper.Design.Set}
     */
    col: Object,

    /**
     * Define zero como valor padrão para células vazias.
     * Quando não definido, o valor padrão é um hífen (-).
     */
    zeroIfEmpty: Boolean,

    /**
     * Ações da célula.
     *
     * Esta propriedade recebe um objeto que contém a estrutura de ações organizadas no menu.
     *
     * @type {Object}
     * @property {Array} all - Todas as ações no menu.
     * @property {Array} main - Ações principais no menu.
     * @property {Array} side - Ações secundárias no menu.
     * @property {Array} rest - Outras ações no menu.
     * @property {Array} categories
     *    - Categorias personalizadas no menu, cada uma contendo um nome e um conjunto de ações.
     *
     * @example
     * {
     *   menu: {
     *     all: [...], // Todas as ações da página
     *     main: [...], // Ações principais da página
     *     side: [...], // Ações secundárias da página
     *     rest: [...], // Outras ações da página
     *     categories: [
     *       { name: 'Category1', actions: [...] }, // Categorias personalizadas
     *       { name: 'Category2', actions: [...] },
     *       // ...
     *     ]
     *   },
     *   pins: {
     *     all: [...], // Todas as ações fixadas da página
     *     main: [...], // Ações principais fixadas da página
     *     side: [...], // Ações secundárias fixadas da página
     *     rest: [...], // Outras ações fixadas da página
     *     categories: [
     *       { name: 'Category1', actions: [...] }, // Categorias personalizadas
     *       { name: 'Category2', actions: [...] },
     *       // ...
     *     ]
     *   },
     *   states: {
     *     all: [...], // Todos os monitores de estado da página
     *     main: [...], // Monitores de estado da página
     *     side: [...], // Monitores de estado secundários da página
     *     rest: [...], // Outros monitores de estado da página
     *     categories: [
     *       { name: 'Category1', actions: [...] }, // Categorias personalizadas
     *       { name: 'Category2', actions: [...] },
     *       // ...
     *     ]
     *   }
     * }
     */
    cellActions: Object,
    /**
     * Ações da linha.
     * Mesma estrutura das ações de célula.
     */
    rowActions: Object,

    /**
     * Coleção dos estilos de formatação dos registros.
     * @type {Array}
     * @example
     *    [
     *      {
     *        ...
     *        "icon": "mdi-account",
     *        "expression": "color: row.online ? 'Success' : 'Trivial';"
     *      }
     *    ]
     *
     */
    states: Array,
  },

  computed: {

    allStates() {
      return [ ...this.states, this.cell, this.row?.['@row'], this.col ].filter(x => x)
    },

    allActions() {
      // Verificando a existência e o conteúdo de links
      const links = this.row['@row']?.links
      if (links?.length > 0) {
        return mergeActions(this.rowActions, links)
      }
      return this.rowActions
    },

    isActionBar() {
      return !!this.colName.match(/^#[\w]+-actions$/)
    },

    isLeftActionBar() {
      return this.isActionBar && this.colName.includes('#left')
    },

    isRightActionBar() {
      return this.isActionBar && this.colName.includes('#right')
    },

    hasMenu() {
      return this.allActions.menu.all.some(action => action.eref || action.href)
    },

    colName() {
      return this.col.value || ''
    },

    pins() {
      /*
       * Campos relevantes dos pins:
       * {
       *   ...
       *   pins: {
       *     ...
       *     main: [...], // Ações a serem fixadas à esquerda.
       *     side: [...], // Ações a serem fixadas à direita.
       *     rest: [...], // Outras ações a serem fixadas à direita.
       *     ...
       *   },
       *   states: {
       *     all: [...], // Todos os monitores de estado da página
       *     main: [...], // Monitores de estado da página
       *     side: [...], // Monitores de estado secundários da página
       *     rest: [...], // Outros monitores de estado da página
       *     categories: [
       *       { name: 'Category1', actions: [...] }, // Categorias personalizadas
       *       { name: 'Category2', actions: [...] },
       *       // ...
       *     ]
       *   }
       * }
       * Note que side e rest são tratados da mesma forma porque não há necessidade de uma terceira
       * forma de pinar as ações, apenas à direita e à esquerda dos dados é suficiente.
       */
      if (this.isLeftActionBar) {
        const pins = this.allActions.pins.main
        const states = this.allActions.states.main
        return [ ...pins, ...states ]
      } else {
        const pins = [...this.allActions.pins.side, ...this.allActions.pins.rest]
        const states = [...this.allActions.states.side, ...this.allActions.states.rest]
        return [ ...pins, ...states ].reverse()
      }
    },

    cell() {
      let cell = this.row?.[this.col.value]
      cell = (typeof cell === "object" ? cell : { value: cell }) || {}
      return cell
    },

    value() {
      return this.cell.value
    },

    formattedValue() {
      const formatter = {
        Boolean: this.formatBoolean,
        Integer: this.formatInteger,
        Long: this.formatLong,
        Decimal: this.formatDecimal,
        Date: this.formatDate,
        Time: this.formatTime,
        DateTime: this.formatDateTime,
        Icon: this.formatIcon,
        TimeSpan: this.formatTimeSpan,
      }[this.col.header?.dataType]
      return formatter?.(this.value) || this.value
    },

    context() {
      return {
        site: this.site,
        eref: parseEref(this.eref),
        page: this.entity,
        data: this.data,
        row: this.row
      }
    },

    options() {
      const { context } = this
      return selectOptions(this.allStates, context)
    },

    iconOnly() {
      return this.col.useIcon
    },

    icon() {
      if (this.col.useIcon) return this.value
      return this.options.icon
    },

    color() {
      return selectColor(this.options.style || this.options.color)
    },

    clazz() {
      return selectClass(this.options.style || this.options.color)
    },

    style() {
      return selectFont(this.options.font)
    },

    baseLink() {
      if (this.cell.links?.length > 0) {
        const link = findRel(this.cell.links, { includes: ['Action', 'Primary'] })
        if (link) return link
      }
      const actions = this.cellActions.menu.all
      if (actions?.length > 0) {
        const link = actions.find(action => action.target?.some(target => target === this.col.value))
        if (link) return link
      }
      return null
    },

    linkOptions() {
      const { baseLink, context } = this
      return selectOptions(baseLink, context) || {}
    },

    hasLink() {
      const { baseLink, linkOptions: { disabled, hidden, invisible } } = this
      return baseLink && !(disabled || hidden || invisible)
    },

    link() {
      return this.hasLink ? this.baseLink : null
    },

    tooltip() {
      const tooltip = {
        enabled: false,
        title: null,
        description: null,
        color: null,
        clazz: null,
        style: null
      }

      const tooltips = this.allStates.map(e => e.tooltip).filter(t => t)
      if (!tooltips.length) return tooltip

      const { context } = this
      const options = selectOptions(tooltips, context)

      tooltip.title = options.title
      tooltip.description = options.description
      tooltip.color = selectColor(options.style || options.color)
      tooltip.clazz = selectClass(options.style || options.color)
      tooltip.style = selectFont(options.font)

      tooltip.enabled = true
      return tooltip
    },
  },

  methods: {
    formatBoolean(value) {
      //
      // Opções de ASCIIs:
      // - ✔ & #x2714;
      // - ✓ & #x2713;
      // - ✖ &#x2716;
      // - ✕ &#x2715;
      // - ✗ &#x2717;
      // - ✘ &#x2718;
      // - × &#x00d7; &times;
      //
      return (value === true || value === 1 || normalizeToLowercase(value) === 'true')
        ? "✓"
        : (value === false || value === 0 || normalizeToLowercase(value) === 'false')
          ? "×" : "—"
    },

    formatInteger(value) {
      if ((value === null || value === undefined) && !this.zeroIfEmpty) return '-'
      value ||= 0
      return value
    },

    formatLong(value) {
      if ((value === null || value === undefined) && !this.zeroIfEmpty) return '-'
      value ||= 0
      return value
    },

    formatDecimal(value) {
      if ((value === null || value === undefined) && !this.zeroIfEmpty) return '-'
      value ||= 0
      const formatter = Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 2,
      })
      return formatter.format(value)
    },

    formatDate(value) {
      if (value === null || value === undefined) return '-'
      return getDate(value)
    },

    formatTime(value) {
      if (value === null || value === undefined) return '-'
      // TODO: Ainda não implementado.
      return value
    },

    formatDateTime(value) {
      if (value === null || value === undefined) return '-'
      return getDateTime(value)?.replace(" 00:00:00", "")
    },

    formatTimeSpan(value) {
      if (value === null || value === undefined) return '-';

      // Regex para capturar dias (opcional), horas, minutos, segundos, e milissegundos (opcional)
      const regex = /^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})(?:\.\d+)?$/;
      const match = value.match(regex);

      if (!match) return value;

      // Extrai os componentes da string baseado no match do regex
      const days = match[1] ? `${match[1]}d ` : '';
      const hours = match[2];
      const minutes = match[3];
      const seconds = match[4];

      // Monta a string formatada
      return `${days}${hours}:${minutes}:${seconds}`;
    },

    formatIcon(value) {
      if (normalizeToLowercase(value) === 'true' || value === 1 ) {
        return { name: "mdi-check" }
      }
      if (normalizeToLowercase(value) === 'false' || value === 1 ) {
        return { name: "mdi-minus", color: "#7777" }
      }
      return {}
    },

    call(link) {
      if (link?.eref) {
        this.$emit("perform", { link, selection: this.row })
        return
      }
      if (link?.href) {
        // window.open(url, windowName, [windowFeatures])
        var window = window.open(link.href, link.href)
        window.addEventListener(
          "load",
          () => {
            var document = document.getElementById()
          },
          true
        )

        return
      }
    },
  },
}
</script>
